<template>
  <div>
    <HrPageLayout
      title="My Redeployment"
      subtitle="Submit requests and manage redeployments"
      icon="🔄"
      :breadcrumb-items="[
        { label: 'Dashboard', route: '/employee-dashboard' },
        { label: 'My Redeployment' }
      ]"
    >
      <template #header-actions>
        <button class="hr-btn hr-btn-primary" @click="showSubmitModal = true" :disabled="loading">
          <i class="bx bx-plus"></i> New Request
        </button>
        <button class="hr-btn hr-btn-secondary" @click="loadData">
          <i class="bx bx-refresh"></i> Refresh
        </button>
      </template>

      <div class="my-redeployment-content">
        <!-- Metrics Cards -->
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-paper-plane"></i></div>
            <p class="hr-metric-label">My Requests</p>
            <h2 class="hr-metric-value">{{ (Array.isArray(requests) ? requests.length : 0) || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-building"></i></div>
            <p class="hr-metric-label">HR Redeployments</p>
            <h2 class="hr-metric-value">{{ (Array.isArray(hrRedeployments) ? hrRedeployments.length : 0) || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
            <p class="hr-metric-label">Pending Review</p>
            <h2 class="hr-metric-value">{{ pendingRequestsCount || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-bell"></i></div>
            <p class="hr-metric-label">Action Required</p>
            <h2 class="hr-metric-value">{{ actionRequiredCount || 0 }}</h2>
          </div>
        </div>

        <!-- Filters -->
        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-body hr-py-3">
            <div class="redeployment-filters">
              <div class="hr-filter-group">
                <label class="hr-form-label">View</label>
                <select v-model="activeTab" class="hr-select" @change="loadData">
                  <option value="my-requests">My Requests</option>
                  <option value="hr-redeployments">HR Redeployments</option>
                </select>
              </div>
              <div class="hr-filter-group" v-if="activeTab === 'my-requests'">
                <label class="hr-form-label">Status</label>
                <select v-model="requestStatusFilter" class="hr-select" @change="filterRequests">
                  <option value="">All Statuses</option>
                  <option value="Pending Review">Pending Review</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Withdrawn">Withdrawn</option>
                </select>
              </div>
              <div class="hr-filter-group" v-if="activeTab === 'hr-redeployments'">
                <label class="hr-form-label">Status</label>
                <select v-model="hrStatusFilter" class="hr-select" @change="filterHrRedeployments">
                  <option value="">All Statuses</option>
                  <option value="Draft">Draft</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Implemented">Implemented</option>
                </select>
              </div>
              <button class="hr-btn hr-btn-primary" @click="loadData">
                <i class="bx bx-refresh"></i> Refresh
              </button>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="hr-loading-wrap hr-py-5">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted hr-mt-2">Loading your redeployments...</p>
        </div>

        <!-- Content Area -->
        <div v-else class="hr-chart-card">
          <div class="hr-chart-body">
            <!-- My Requests Table -->
            <div v-if="activeTab === 'my-requests'">
              <div v-if="!filteredRequests.length" class="hr-text-center hr-py-5">
                <i class="bx bx-inbox" style="font-size: 48px; color: #3b82f6;"></i>
                <h5 class="hr-mt-4">No requests found</h5>
                <p class="hr-text-muted">You haven't submitted any redeployment requests yet.</p>
                <button class="hr-btn hr-btn-primary hr-mt-3" @click="showSubmitModal = true">
                  <i class="bx bx-plus"></i> Submit Your First Request
                </button>
              </div>

              <div v-else class="hr-table-wrapper">
                <table class="hr-table hr-table-hover">
                  <thead>
                    <tr>
                      <th>Request ID</th>
                      <th>Target Department</th>
                      <th>Preferred Date</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th class="hr-text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="request in filteredRequests" :key="request.id">
                      <td>
                        <strong>{{ request.request_id }}</strong>
                      </td>
                      <td>
                        {{ request.target_department_name }}
                        <div class="hr-text-muted hr-small" v-if="request.preferred_location">
                          {{ request.preferred_location }}
                        </div>
                      </td>
                      <td>{{ formatDate(request.preferred_effective_date) }}</td>
                      <td>
                        <span class="hr-badge" :class="getStatusBadgeClass(request.status)">
                          {{ request.status }}
                        </span>
                      </td>
                      <td>{{ formatDate(request.submitted_at) }}</td>
                      <td class="hr-text-end">
                        <button type="button" @click="viewRequest(request)" class="hr-btn hr-btn-sm hr-btn-primary hr-mr-1">
                          <i class="bx bx-show"></i> View
                        </button>
                        <button 
                          v-if="request.status === 'Pending Review'"
                          type="button" 
                          @click="editRequest(request)" 
                          class="hr-btn hr-btn-sm hr-btn-secondary hr-mr-1"
                        >
                          <i class="bx bx-edit"></i> Edit
                        </button>
                        <button 
                          v-if="request.status === 'Pending Review'"
                          type="button" 
                          @click="withdrawRequest(request)" 
                          class="hr-btn hr-btn-sm hr-btn-danger"
                        >
                          <i class="bx bx-x"></i> Withdraw
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- HR Redeployments Table -->
            <div v-if="activeTab === 'hr-redeployments'">
              <div v-if="!filteredHrRedeployments.length" class="hr-text-center hr-py-5">
                <i class="bx bx-building" style="font-size: 48px; color: #3b82f6;"></i>
                <h5 class="hr-mt-4">No HR redeployments found</h5>
                <p class="hr-text-muted">No redeployments have been created for you by HR.</p>
              </div>

              <div v-else class="hr-table-wrapper">
                <table class="hr-table hr-table-hover">
                  <thead>
                    <tr>
                      <th>Redeployment</th>
                      <th>From → To</th>
                      <th>Effective Date</th>
                      <th>Status</th>
                      <th>Acknowledged</th>
                      <th class="hr-text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="redeployment in filteredHrRedeployments" :key="redeployment.id" 
                        :class="{ 'hr-row-warning': !redeployment.acknowledged_at }">
                      <td>
                        <strong>HR-{{ redeployment.id }}</strong>
                        <div class="hr-text-muted hr-small" v-if="redeployment.station">
                          Station: {{ redeployment.station }}
                        </div>
                      </td>
                      <td>
                        <div class="department-flow">
                          <span class="from-dept">{{ redeployment.current_department_name || 'Current' }}</span>
                          <i class="bx bx-right-arrow-alt"></i>
                          <span class="to-dept">{{ redeployment.target_department_name }}</span>
                        </div>
                      </td>
                      <td>{{ formatDate(redeployment.effective_date) }}</td>
                      <td>
                        <span class="hr-badge" :class="getHrRedeploymentStatusClass(redeployment)">
                          {{ redeployment.status }}
                        </span>
                      </td>
                      <td>
                        <span v-if="redeployment.acknowledged_at" class="hr-badge hr-badge-success">
                          <i class="bx bx-check"></i> {{ formatDate(redeployment.acknowledged_at) }}
                        </span>
                        <span v-else class="hr-badge hr-badge-warning">
                          <i class="bx bx-time"></i> Pending
                        </span>
                      </td>
                      <td class="hr-text-end">
                        <button type="button" @click="viewHrRedeployment(redeployment)" class="hr-btn hr-btn-sm hr-btn-primary hr-mr-1">
                          <i class="bx bx-show"></i> View
                        </button>
                        <button 
                          v-if="!redeployment.acknowledged_at"
                          type="button" 
                          @click="acknowledgeRedeployment(redeployment)" 
                          class="hr-btn hr-btn-sm hr-btn-success hr-mr-1"
                          :disabled="acknowledging"
                        >
                          <i class="bx bx-check"></i> Acknowledge
                        </button>
                        <button 
                          type="button" 
                          @click="provideFeedback(redeployment)" 
                          class="hr-btn hr-btn-sm hr-btn-secondary"
                        >
                          <i class="bx bx-message"></i> Feedback
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h4 class="mb-1">My Redeployment</h4>
        <p class="text-muted mb-0">Submit requests and manage redeployments</p>
      </div>
      <button 
        class="btn btn-primary"
        @click="showSubmitModal = true"
        :disabled="loading"
      >
        <i class="fas fa-plus me-2"></i>
        New Request
      </button>
    </div>

    <!-- Tabs -->
    <ul class="nav nav-tabs mb-4" role="tablist">
      <li class="nav-item" role="presentation">
        <button 
          class="nav-link" 
          :class="{ active: activeTab === 'my-requests' }"
          @click="activeTab = 'my-requests'"
          type="button"
        >
          <i class="fas fa-paper-plane me-2"></i>
          My Requests
          <span v-if="Array.isArray(requests) && requests.length > 0" class="badge bg-primary ms-2">{{ requests.length }}</span>
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button 
          class="nav-link" 
          :class="{ active: activeTab === 'hr-redeployments' }"
          @click="activeTab = 'hr-redeployments'"
          type="button"
        >
          <i class="fas fa-building me-2"></i>
          HR Redeployments
          <span v-if="Array.isArray(hrRedeployments) && hrRedeployments.length > 0" class="badge bg-warning ms-2">{{ hrRedeployments.length }}</span>
        </button>
      </li>
    </ul>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading your requests...</p>
    </div>

    <!-- Tab Content -->
    <div class="tab-content">
      <!-- My Requests Tab -->
      <div v-show="activeTab === 'my-requests'">
        <!-- No Requests -->
        <div v-if="!Array.isArray(requests) || requests.length === 0" class="text-center py-5">
          <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
          <h5 class="text-muted">No Redeployment Requests</h5>
          <p class="text-muted">You haven't submitted any redeployment requests yet.</p>
          <button 
            class="btn btn-primary"
            @click="showSubmitModal = true"
          >
            Submit Your First Request
          </button>
        </div>

        <!-- Requests Cards -->
        <div v-else class="row">
          <div 
            v-for="request in requests" 
            :key="request.id"
            class="col-md-6 col-lg-4 mb-4"
          >
            <div class="card h-100">
              <div class="card-header d-flex justify-content-between align-items-center">
                <small class="text-muted">{{ request.request_id }}</small>
                <span :class="getStatusBadgeClass(request.status)">
                  {{ request.status }}
                </span>
              </div>
              <div class="card-body">
                <h6 class="card-title">{{ request.target_department_name }}</h6>
                <p class="card-text text-muted small">
                  <i class="fas fa-calendar me-1"></i>
                  Preferred Date: {{ formatDate(request.preferred_effective_date) }}
                </p>
                <p class="card-text text-muted small">
                  <i class="fas fa-clock me-1"></i>
                  Submitted: {{ formatDate(request.submitted_at) }}
                </p>
                <p class="card-text">
                  {{ truncateText(request.justification, 100) }}
                </p>
              </div>
              <div class="card-footer">
                <div class="btn-group w-100" role="group">
                  <button 
                    class="btn btn-outline-primary btn-sm"
                    @click="viewRequest(request)"
                  >
                    <i class="fas fa-eye me-1"></i>
                    View
                  </button>
                  <button 
                    v-if="request.status === 'Pending Review'"
                    class="btn btn-outline-secondary btn-sm"
                    @click="editRequest(request)"
                  >
                    <i class="fas fa-edit me-1"></i>
                    Edit
                  </button>
                  <button 
                    v-if="request.status === 'Pending Review'"
                    class="btn btn-outline-danger btn-sm"
                    @click="withdrawRequest(request)"
                  >
                    <i class="fas fa-times me-1"></i>
                    Withdraw
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- HR Redeployments Tab -->
      <div v-show="activeTab === 'hr-redeployments'">
        <!-- No HR Redeployments -->
        <div v-if="!Array.isArray(hrRedeployments) || hrRedeployments.length === 0" class="text-center py-5">
          <i class="fas fa-building fa-3x text-muted mb-3"></i>
          <h5 class="text-muted">No HR Redeployments</h5>
          <p class="text-muted">No redeployments have been created for you by HR.</p>
        </div>

        <!-- HR Redeployments Cards -->
        <div v-else class="row">
          <div 
            v-for="redeployment in hrRedeployments" 
            :key="redeployment.id"
            class="col-md-6 col-lg-4 mb-4"
          >
            <div class="card h-100" :class="{ 'border-warning': !redeployment.acknowledged_at }">
              <div class="card-header d-flex justify-content-between align-items-center">
                <small class="text-muted">HR-{{ redeployment.id }}</small>
                <div>
                  <span :class="getHrRedeploymentStatusClass(redeployment)">
                    {{ redeployment.status }}
                  </span>
                  <span v-if="!redeployment.acknowledged_at" class="badge bg-warning ms-1">
                    Action Required
                  </span>
                </div>
              </div>
              <div class="card-body">
                <h6 class="card-title">
                  <i class="fas fa-arrow-right me-2"></i>
                  {{ redeployment.target_department_name }}
                </h6>
                <p class="card-text text-muted small">
                  <i class="fas fa-building me-1"></i>
                  From: {{ redeployment.current_department_name || 'Not specified' }}
                </p>
                <p class="card-text text-muted small">
                  <i class="fas fa-calendar me-1"></i>
                  Effective: {{ formatDate(redeployment.effective_date) }}
                </p>
                <p class="card-text text-muted small">
                  <i class="fas fa-map-marker-alt me-1"></i>
                  Station: {{ redeployment.station || 'Not specified' }}
                </p>
                <p class="card-text">
                  {{ truncateText(redeployment.reason, 100) }}
                </p>
              </div>
              <div class="card-footer">
                <div class="btn-group w-100" role="group">
                  <button 
                    class="btn btn-outline-primary btn-sm"
                    @click="viewHrRedeployment(redeployment)"
                  >
                    <i class="fas fa-eye me-1"></i>
                    View
                  </button>
                  <button 
                    v-if="!redeployment.acknowledged_at"
                    class="btn btn-success btn-sm"
                    @click="acknowledgeRedeployment(redeployment)"
                    :disabled="acknowledging"
                  >
                    <i class="fas fa-check me-1"></i>
                    Acknowledge
                  </button>
                  <button 
                    class="btn btn-outline-secondary btn-sm"
                    @click="provideFeedback(redeployment)"
                  >
                    <i class="fas fa-comment me-1"></i>
                    Feedback
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Submit/Edit Request Modal -->
    <div 
      class="modal fade" 
      :class="{ show: showSubmitModal }" 
      :style="{ display: showSubmitModal ? 'block' : 'none' }"
      tabindex="-1"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingRequest ? 'Edit Redeployment Request' : 'New Redeployment Request' }}
            </h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="closeSubmitModal"
            ></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitRequest">
              <!-- Target Department -->
              <div class="mb-3">
                <label for="targetDepartment" class="form-label">
                  Target Department <span class="text-danger">*</span>
                </label>
                <select 
                  id="targetDepartment"
                  v-model="requestForm.targetDepartmentId"
                  class="form-select"
                  required
                >
                  <option value="">Select Department</option>
                  <option 
                    v-for="dept in departments" 
                    :key="dept.id" 
                    :value="dept.id"
                  >
                    {{ dept.departmentName }}
                  </option>
                </select>
              </div>

              <!-- Preferred Location -->
              <div class="mb-3">
                <label for="preferredLocation" class="form-label">
                  Preferred Location (Optional)
                </label>
                <input 
                  id="preferredLocation"
                  v-model="requestForm.preferredLocation"
                  type="text"
                  class="form-control"
                  placeholder="e.g., Nairobi Office, Mombasa Branch"
                >
              </div>

              <!-- Preferred Effective Date -->
              <div class="mb-3">
                <label for="effectiveDate" class="form-label">
                  Preferred Effective Date <span class="text-danger">*</span>
                </label>
                <input 
                  id="effectiveDate"
                  v-model="requestForm.preferredEffectiveDate"
                  type="date"
                  class="form-control"
                  :min="minEffectiveDate"
                  required
                >
                <div class="form-text">
                  Must be at least 30 days from today
                </div>
              </div>

              <!-- Justification -->
              <div class="mb-3">
                <label for="justification" class="form-label">
                  Justification <span class="text-danger">*</span>
                </label>
                <textarea 
                  id="justification"
                  v-model="requestForm.justification"
                  class="form-control"
                  rows="5"
                  placeholder="Please provide detailed reasons for your redeployment request..."
                  required
                  minlength="50"
                  maxlength="2000"
                ></textarea>
                <div class="form-text">
                  {{ requestForm.justification.length }}/2000 characters (minimum 50)
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="closeSubmitModal"
              :disabled="submitting"
            >
              Cancel
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="submitRequest"
              :disabled="submitting || !isFormValid"
            >
              <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingRequest ? 'Update Request' : 'Submit Request' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- View Request Modal -->
    <div 
      class="modal fade" 
      :class="{ show: showViewModal }" 
      :style="{ display: showViewModal ? 'block' : 'none' }"
      tabindex="-1"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Request Details</h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="showViewModal = false"
            ></button>
          </div>
          <div class="modal-body" v-if="selectedRequest || selectedHrRedeployment">
            <div class="row" v-if="selectedRequest">
              <div class="col-md-6">
                <h6>Request Information</h6>
                <table class="table table-sm">
                  <tr>
                    <td><strong>Request ID:</strong></td>
                    <td>{{ selectedRequest.request_id }}</td>
                  </tr>
                  <tr>
                    <td><strong>Status:</strong></td>
                    <td>
                      <span :class="getStatusBadgeClass(selectedRequest.status)">
                        {{ selectedRequest.status }}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Target Department:</strong></td>
                    <td>{{ selectedRequest.target_department_name }}</td>
                  </tr>
                  <tr>
                    <td><strong>Preferred Location:</strong></td>
                    <td>{{ selectedRequest.preferred_location || 'Not specified' }}</td>
                  </tr>
                  <tr>
                    <td><strong>Preferred Date:</strong></td>
                    <td>{{ formatDate(selectedRequest.preferred_effective_date) }}</td>
                  </tr>
                  <tr>
                    <td><strong>Submitted:</strong></td>
                    <td>{{ formatDate(selectedRequest.submitted_at) }}</td>
                  </tr>
                </table>
              </div>
              <div class="col-md-6">
                <h6>Justification</h6>
                <p class="text-muted">{{ selectedRequest.justification }}</p>
                
                <div v-if="selectedRequest.hr_comments">
                  <h6>HR Comments</h6>
                  <p class="text-muted">{{ selectedRequest.hr_comments }}</p>
                </div>
              </div>
            </div>

            <div class="row" v-if="selectedHrRedeployment">
              <div class="col-md-6">
                <h6>Redeployment Information</h6>
                <table class="table table-sm">
                  <tr>
                    <td><strong>Redeployment ID:</strong></td>
                    <td>HR-{{ selectedHrRedeployment.id }}</td>
                  </tr>
                  <tr>
                    <td><strong>Status:</strong></td>
                    <td>
                      <span :class="getHrRedeploymentStatusClass(selectedHrRedeployment)">
                        {{ selectedHrRedeployment.status }}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>From Department:</strong></td>
                    <td>{{ selectedHrRedeployment.current_department_name || 'Not specified' }}</td>
                  </tr>
                  <tr>
                    <td><strong>To Department:</strong></td>
                    <td>{{ selectedHrRedeployment.target_department_name }}</td>
                  </tr>
                  <tr>
                    <td><strong>Effective Date:</strong></td>
                    <td>{{ formatDate(selectedHrRedeployment.effective_date) }}</td>
                  </tr>
                  <tr>
                    <td><strong>Station:</strong></td>
                    <td>{{ selectedHrRedeployment.station || 'Not specified' }}</td>
                  </tr>
                  <tr>
                    <td><strong>Acknowledged:</strong></td>
                    <td>
                      <span v-if="selectedHrRedeployment.acknowledged_at" class="text-success">
                        <i class="fas fa-check me-1"></i>
                        {{ formatDate(selectedHrRedeployment.acknowledged_at) }}
                      </span>
                      <span v-else class="text-warning">
                        <i class="fas fa-clock me-1"></i>
                        Pending Acknowledgment
                      </span>
                    </td>
                  </tr>
                </table>
              </div>
              <div class="col-md-6">
                <h6>Reason for Redeployment</h6>
                <p class="text-muted">{{ selectedHrRedeployment.reason || 'No reason provided' }}</p>
                
                <div v-if="selectedHrRedeployment.feedback_text">
                  <h6>Your Feedback</h6>
                  <p class="text-muted">{{ selectedHrRedeployment.feedback_text }}</p>
                </div>
                
                <div v-if="selectedHrRedeployment.appeal_text">
                  <h6>Your Appeal</h6>
                  <p class="text-muted">{{ selectedHrRedeployment.appeal_text }}</p>
                </div>
              </div>
            </div>

            <!-- Status History -->
            <div v-if="requestHistory.length > 0" class="mt-4">
              <h6>Status History</h6>
              <div class="timeline">
                <div 
                  v-for="(history, index) in requestHistory" 
                  :key="index"
                  class="timeline-item"
                >
                  <div class="timeline-marker"></div>
                  <div class="timeline-content">
                    <h6 class="mb-1">{{ history.new_status }}</h6>
                    <p class="text-muted mb-1">{{ history.comments }}</p>
                    <small class="text-muted">
                      {{ formatDate(history.changed_at) }} by {{ history.changed_by_username }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Withdraw Request Modal -->
    <div 
      class="modal fade" 
      :class="{ show: showWithdrawModal }" 
      :style="{ display: showWithdrawModal ? 'block' : 'none' }"
      tabindex="-1"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Withdraw Request</h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="showWithdrawModal = false"
            ></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to withdraw this redeployment request?</p>
            <div class="mb-3">
              <label for="withdrawalReason" class="form-label">
                Reason for Withdrawal <span class="text-danger">*</span>
              </label>
              <textarea 
                id="withdrawalReason"
                v-model="withdrawalReason"
                class="form-control"
                rows="3"
                placeholder="Please provide reason for withdrawal..."
                required
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="showWithdrawModal = false"
              :disabled="withdrawing"
            >
              Cancel
            </button>
            <button 
              type="button" 
              class="btn btn-danger" 
              @click="confirmWithdraw"
              :disabled="withdrawing || !withdrawalReason.trim()"
            >
              <span v-if="withdrawing" class="spinner-border spinner-border-sm me-2"></span>
              Withdraw Request
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Feedback Modal -->
    <div 
      class="modal fade" 
      :class="{ show: showFeedbackModal }" 
      :style="{ display: showFeedbackModal ? 'block' : 'none' }"
      tabindex="-1"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Provide Feedback</h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="showFeedbackModal = false"
            ></button>
          </div>
          <div class="modal-body" v-if="hrRedeploymentToAcknowledge">
            <div class="alert alert-info">
              <h6>Redeployment: {{ hrRedeploymentToAcknowledge.target_department_name }}</h6>
              <p class="mb-0">Effective Date: {{ formatDate(hrRedeploymentToAcknowledge.effective_date) }}</p>
            </div>
            
            <form @submit.prevent="submitFeedback">
              <!-- Feedback -->
              <div class="mb-3">
                <label for="feedbackText" class="form-label">
                  Feedback
                </label>
                <textarea 
                  id="feedbackText"
                  v-model="feedbackText"
                  class="form-control"
                  rows="4"
                  placeholder="Provide your feedback about this redeployment..."
                ></textarea>
                <div class="form-text">
                  Share your thoughts, concerns, or questions about this redeployment.
                </div>
              </div>

              <!-- Appeal -->
              <div class="mb-3">
                <label for="appealText" class="form-label">
                  Appeal (Optional)
                </label>
                <textarea 
                  id="appealText"
                  v-model="appealText"
                  class="form-control"
                  rows="4"
                  placeholder="If you wish to appeal this redeployment, provide your reasons..."
                ></textarea>
                <div class="form-text">
                  If you disagree with this redeployment, explain your reasons for appeal.
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="showFeedbackModal = false"
              :disabled="submitting"
            >
              Cancel
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="submitFeedback"
              :disabled="submitting || (!feedbackText.trim() && !appealText.trim())"
            >
              <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
              Submit Feedback
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Backdrop -->
    <div 
      v-if="showSubmitModal || showViewModal || showWithdrawModal || showFeedbackModal"
      class="modal-backdrop fade show"
      @click="closeAllModals"
    ></div>
  </div>
</template>

<script>
import { execute, authUrl } from '@/api'
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue"

export default {
  name: 'MyRedeployment',
  components: { HrPageLayout },
  data() {
    return {
      loading: true,
      submitting: false,
      withdrawing: false,
      acknowledging: false,
      requests: [], // Employee-initiated requests
      hrRedeployments: [], // HR-initiated redeployments
      departments: [],
      activeTab: 'my-requests', // 'my-requests' or 'hr-redeployments'
      requestStatusFilter: '',
      hrStatusFilter: '',
      
      // Modals
      showSubmitModal: false,
      showViewModal: false,
      showWithdrawModal: false,
      showFeedbackModal: false,
      
      // Forms
      requestForm: {
        targetDepartmentId: '',
        preferredLocation: '',
        preferredEffectiveDate: '',
        justification: ''
      },
      
      // Selected items
      selectedRequest: null,
      selectedHrRedeployment: null,
      editingRequest: null,
      requestToWithdraw: null,
      hrRedeploymentToAcknowledge: null,
      withdrawalReason: '',
      feedbackText: '',
      appealText: '',
      requestHistory: []
    }
  },
  
  computed: {
    minEffectiveDate() {
      const date = new Date()
      date.setDate(date.getDate() + 30)
      return date.toISOString().split('T')[0]
    },
    
    isFormValid() {
      return this.requestForm.targetDepartmentId &&
             this.requestForm.preferredEffectiveDate &&
             this.requestForm.justification.length >= 50 &&
             this.requestForm.justification.length <= 2000
    },
    
    filteredRequests() {
      if (!Array.isArray(this.requests)) return []
      if (!this.requestStatusFilter) return this.requests
      return this.requests.filter(request => request.status === this.requestStatusFilter)
    },
    
    filteredHrRedeployments() {
      if (!Array.isArray(this.hrRedeployments)) return []
      if (!this.hrStatusFilter) return this.hrRedeployments
      return this.hrRedeployments.filter(redeployment => redeployment.status === this.hrStatusFilter)
    },
    
    pendingRequestsCount() {
      if (!Array.isArray(this.requests)) return 0
      return this.requests.filter(request => request.status === 'Pending Review').length
    },
    
    actionRequiredCount() {
      if (!Array.isArray(this.hrRedeployments)) return 0
      return this.hrRedeployments.filter(redeployment => !redeployment.acknowledged_at).length
    }
  },
  
  mounted() {
    this.loadData()
  },
  
  methods: {
    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.loadRequests(),
          this.loadHrRedeployments(),
          this.loadDepartments()
        ])
      } catch (error) {
        console.error('Error loading data:', error)
        if (this.$toast) {
          this.$toast.error('Failed to load data')
        } else {
          alert('Failed to load data')
        }
      } finally {
        this.loading = false
      }
    },
    
    async loadRequests() {
      try {
        const data = new FormData()
        data.append('function', 'getMyRedeploymentRequests')
        const response = await execute(data, authUrl)
        if (response.data.success) {
          this.requests = Array.isArray(response.data.data.requests) ? response.data.data.requests : []
        } else {
          throw new Error(response.data.message)
        }
      } catch (error) {
        console.error('Error loading requests:', error)
        // Ensure requests is always an array
        this.requests = []
        if (this.$toast) {
          this.$toast.error('Failed to load requests')
        } else {
          alert('Failed to load requests')
        }
      }
    },
    
    async loadHrRedeployments() {
      try {
        const data = new FormData()
        data.append('function', 'getMyRedeploymentItems')
        const response = await execute(data, authUrl)
        if (response.data.success) {
          // The backend returns the array directly as 'data', not nested in 'data.data'
          this.hrRedeployments = Array.isArray(response.data.data) ? response.data.data : []
        } else {
          throw new Error(response.data.message)
        }
      } catch (error) {
        console.error('Error loading HR redeployments:', error)
        // Ensure hrRedeployments is always an array
        this.hrRedeployments = []
        if (this.$toast) {
          this.$toast.error('Failed to load HR redeployments')
        } else {
          alert('Failed to load HR redeployments')
        }
      }
    },
    
    async loadDepartments() {
      try {
        const data = new FormData()
        data.append('function', 'getDepartmentsForRedeployment')
        const response = await execute(data, authUrl)
        if (response.data.success) {
          const payload = response.data.data
          this.departments = Array.isArray(payload?.departments) ? payload.departments : []
        } else {
          console.warn('Departments API:', response.data.message)
          this.departments = []
        }
      } catch (error) {
        console.error('Error loading departments:', error)
        // Ensure departments is always an array
        this.departments = []
        if (this.$toast) {
          this.$toast.error('Failed to load departments')
        } else {
          alert('Failed to load departments')
        }
      }
    },
    
    async submitRequest() {
      if (!this.isFormValid) return
      
      this.submitting = true
      try {
        const functionName = this.editingRequest ? 'updateRedeploymentRequest' : 'submitRedeploymentRequest'
        const data = new FormData()
        data.append('function', functionName)
        data.append('targetDepartmentId', this.requestForm.targetDepartmentId)
        data.append('preferredLocation', this.requestForm.preferredLocation)
        data.append('preferredEffectiveDate', this.requestForm.preferredEffectiveDate)
        data.append('justification', this.requestForm.justification)
        
        if (this.editingRequest) {
          data.append('requestId', this.editingRequest.id)
        }
        
        const response = await execute(data, authUrl)
        
        if (response.data.success) {
          if (this.$toast) {
            this.$toast.success(response.data.message)
          } else {
            alert(response.data.message)
          }
          this.closeSubmitModal()
          await this.loadRequests()
        } else {
          throw new Error(response.data.message)
        }
      } catch (error) {
        console.error('Error submitting request:', error)
        if (this.$toast) {
          this.$toast.error(error.message || 'Failed to submit request')
        } else {
          alert(error.message || 'Failed to submit request')
        }
      } finally {
        this.submitting = false
      }
    },
    
    async viewRequest(request) {
      try {
        const data = new FormData()
        data.append('function', 'getRedeploymentRequestDetails')
        data.append('requestId', request.id)
        const response = await execute(data, authUrl)
        
        if (response.data.success) {
          this.selectedRequest = response.data.data.request
          this.requestHistory = response.data.data.history
          this.showViewModal = true
        } else {
          throw new Error(response.data.message)
        }
      } catch (error) {
        console.error('Error loading request details:', error)
        if (this.$toast) {
          this.$toast.error('Failed to load request details')
        } else {
          alert('Failed to load request details')
        }
      }
    },
    
    editRequest(request) {
      this.editingRequest = request
      this.requestForm = {
        targetDepartmentId: request.target_department_id,
        preferredLocation: request.preferred_location || '',
        preferredEffectiveDate: request.preferred_effective_date,
        justification: request.justification
      }
      this.showSubmitModal = true
    },
    
    withdrawRequest(request) {
      this.requestToWithdraw = request
      this.withdrawalReason = ''
      this.showWithdrawModal = true
    },
    
    async confirmWithdraw() {
      if (!this.withdrawalReason.trim()) return
      
      this.withdrawing = true
      try {
        const data = new FormData()
        data.append('function', 'withdrawRedeploymentRequest')
        data.append('requestId', this.requestToWithdraw.id)
        data.append('withdrawalReason', this.withdrawalReason)
        const response = await execute(data, authUrl)
        
        if (response.data.success) {
          if (this.$toast) {
            this.$toast.success(response.data.message)
          } else {
            alert(response.data.message)
          }
          this.showWithdrawModal = false
          await this.loadRequests()
        } else {
          throw new Error(response.data.message)
        }
      } catch (error) {
        console.error('Error withdrawing request:', error)
        if (this.$toast) {
          this.$toast.error(error.message || 'Failed to withdraw request')
        } else {
          alert(error.message || 'Failed to withdraw request')
        }
      } finally {
        this.withdrawing = false
      }
    },
    
    closeSubmitModal() {
      this.showSubmitModal = false
      this.editingRequest = null
      this.requestForm = {
        targetDepartmentId: '',
        preferredLocation: '',
        preferredEffectiveDate: '',
        justification: ''
      }
    },
    
    closeAllModals() {
      this.showSubmitModal = false
      this.showViewModal = false
      this.showWithdrawModal = false
      this.showFeedbackModal = false
      this.selectedRequest = null
      this.selectedHrRedeployment = null
    },
    
    getStatusBadgeClass(status) {
      const classes = {
        'Pending Review': 'hr-badge-warning',
        'Under Review': 'hr-badge-info',
        'Approved': 'hr-badge-success',
        'Rejected': 'hr-badge-danger',
        'Withdrawn': 'hr-badge-secondary',
        'Implemented': 'hr-badge-primary'
      }
      return classes[status] || 'hr-badge-secondary'
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    
    truncateText(text, length) {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    },
    
    getHrRedeploymentStatusClass(redeployment) {
      const classes = {
        'Draft': 'hr-badge-secondary',
        'Pending': 'hr-badge-warning',
        'Approved': 'hr-badge-success',
        'Rejected': 'hr-badge-danger',
        'Implemented': 'hr-badge-primary'
      }
      return classes[redeployment.status] || 'hr-badge-secondary'
    },
    
    filterRequests() {
      // Filtering is handled by computed property
    },
    
    filterHrRedeployments() {
      // Filtering is handled by computed property
    },
    
    async viewHrRedeployment(redeployment) {
      this.selectedHrRedeployment = redeployment
      this.showViewModal = true
    },
    
    async acknowledgeRedeployment(redeployment) {
      this.acknowledging = true
      try {
        const data = new FormData()
        data.append('function', 'acknowledgeMyRedeployment')
        data.append('id', redeployment.id)
        const response = await execute(data, authUrl)
        
        if (response.data.success) {
          if (this.$toast) {
            this.$toast.success('Redeployment acknowledged successfully')
          } else {
            alert('Redeployment acknowledged successfully')
          }
          await this.loadHrRedeployments()
        } else {
          throw new Error(response.data.message)
        }
      } catch (error) {
        console.error('Error acknowledging redeployment:', error)
        if (this.$toast) {
          this.$toast.error(error.message || 'Failed to acknowledge redeployment')
        } else {
          alert(error.message || 'Failed to acknowledge redeployment')
        }
      } finally {
        this.acknowledging = false
      }
    },
    
    provideFeedback(redeployment) {
      this.hrRedeploymentToAcknowledge = redeployment
      this.feedbackText = redeployment.feedback_text || ''
      this.appealText = redeployment.appeal_text || ''
      this.showFeedbackModal = true
    },
    
    async submitFeedback() {
      if (!this.feedbackText.trim() && !this.appealText.trim()) {
        if (this.$toast) {
          this.$toast.error('Please provide feedback or appeal text')
        } else {
          alert('Please provide feedback or appeal text')
        }
        return
      }
      
      this.submitting = true
      try {
        const data = new FormData()
        data.append('function', 'submitMyRedeploymentFeedback')
        data.append('id', this.hrRedeploymentToAcknowledge.id)
        data.append('feedback_text', this.feedbackText)
        data.append('appeal_text', this.appealText)
        const response = await execute(data, authUrl)
        
        if (response.data.success) {
          if (this.$toast) {
            this.$toast.success('Feedback submitted successfully')
          } else {
            alert('Feedback submitted successfully')
          }
          this.showFeedbackModal = false
          await this.loadHrRedeployments()
        } else {
          throw new Error(response.data.message)
        }
      } catch (error) {
        console.error('Error submitting feedback:', error)
        if (this.$toast) {
          this.$toast.error(error.message || 'Failed to submit feedback')
        } else {
          alert(error.message || 'Failed to submit feedback')
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.redeployment-filters { 
  display: flex; 
  flex-wrap: wrap; 
  gap: 12px; 
  align-items: flex-end; 
}

.hr-text-end { 
  text-align: right; 
}

.hr-mr-1 { 
  margin-right: 8px; 
}

.department-flow {
  display: flex;
  align-items: center;
  gap: 8px;
}

.from-dept {
  color: #6b7280;
  font-size: 0.875rem;
}

.to-dept {
  font-weight: 600;
  color: #1f2937;
}

.hr-row-warning {
  background-color: #fef3c7 !important;
}

.hr-row-warning:hover {
  background-color: #fde68a !important;
}

/* Modal styles for compatibility */
.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

.btn-group .btn {
  flex: 1;
}

.timeline {
  position: relative;
  padding-left: 30px;
}

.timeline-item {
  position: relative;
  margin-bottom: 20px;
}

.timeline-marker {
  position: absolute;
  left: -35px;
  top: 5px;
  width: 10px;
  height: 10px;
  background-color: #007bff;
  border-radius: 50%;
}

.timeline-marker::before {
  content: '';
  position: absolute;
  left: 4px;
  top: 10px;
  width: 2px;
  height: 30px;
  background-color: #dee2e6;
}

.timeline-item:last-child .timeline-marker::before {
  display: none;
}

.timeline-content {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border-left: 3px solid #007bff;
}
</style>